import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AllReceivingInvoicesViewModel } from "../../../viewmodels/receiving/invoices/AllReceivingInvoicesViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";

import { formatDate, withRouter } from "../../../shared/utility";

import EmptyModal from "../../../components/modals/EmptyModal";
import SlideOver from "../../../components/slideOver/SlideOver";
import ActionButton from "../../../components/controls/ActionButton";
import ActionLink from "../../../components/controls/ActionLink";
import Button from "../../../components/ui/Button";

import { Paginator } from "primereact/paginator";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import DateRangeFilter from "../../../components/filterDropdown/DateRangeFilter";
import { formatDateRangeSummary } from "../../../shared/filterUtils";
import AuditHistoriesLink from "../../../components/audit/AuditHistoriesLink";
import OperationalTableToolbar from "../../../components/operational/OperationalTableToolbar";
import OperationalDataTableSkeleton from "../../../components/operational/OperationalDataTableSkeleton";

const SEARCH_DEBOUNCE_MS = 400;

function AllReceivingInvoices(props: any) {
  const { addSnackbar, logout } = props;
  const {
    askDeleteReceivingInvoice,
    emptyModal,
    getReceivingInvoices,
    resetFilters,
    loading,
    receivingInvoices,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    filters,
    setFilters,
    showDates,
    setShowDates,
    search,
  } = AllReceivingInvoicesViewModel(logout, addSnackbar) as any;

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSearchValue = () => searchRef.current?.value?.trim() || "";

  useEffect(() => {
    getReceivingInvoices("", 1);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleSearch = (filterOverride = filters) => {
    setFirst(0);
    getReceivingInvoices(getSearchValue(), 1, filterOverride);
  };

  const scheduleSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      handleSearch();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePagination = (event: any) => {
    const currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getReceivingInvoices(getSearchValue(), currentPage, filters);
  };

  const applyDateFilter = (startDate: any, endDate: any) => {
    const nextFilters = {
      ...filters,
      startDate: startDate !== undefined ? startDate : filters.startDate,
      endDate: endDate !== undefined ? endDate : filters.endDate,
    };
    setFilters(nextFilters);
    handleSearch(nextFilters);
  };

  const clearDates = () => {
    const nextFilters = {
      ...filters,
      startDate: null,
      endDate: null,
    };
    setFilters(nextFilters);
    handleSearch(nextFilters);
  };

  const handleClearAll = () => {
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    setFirst(0);
    resetFilters();
  };

  const totalRecords = receivingInvoices?.total ?? 0;
  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );
  const hasSearch = Boolean(search);
  const hasAnyFilters = hasSearch || !hasDateFilter;
  const resultMeta = `${totalRecords.toLocaleString()} result${totalRecords === 1 ? "" : "s"}`;

  const invoiceNumberBody = (row: any) => row.invoice_number || "—";
  const poNumberBody = (row: any) => row.purchase_order?.po_number || "—";
  const supplierBody = (row: any) => row.supplier?.name || "—";
  const receivedByBody = (row: any) =>
    row.received_by_user
      ? row.received_by_user.name
      : row.recorded_by_user?.username || "—";
  const dateBody = (row: any) => formatDate(row.date);
  const actionsBody = (row: any, options: any) => (
    <div className="flex flex-wrap items-center gap-2">
      <ActionLink
        to={`/receiving-invoices/${row.id}`}
        variant="primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        Edit
      </ActionLink>
      <ActionButton
        color="red"
        onClick={() => {
          askDeleteReceivingInvoice(row, options?.rowIndex);
        }}
      >
        Delete
      </ActionButton>
    </div>
  );

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No receiving invoices match the current filters.
      </p>
      {hasAnyFilters ? (
        <Button
          type="button"
          variant="soft"
          size="md"
          className="mt-4"
          onClick={handleClearAll}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );

  const tableSkeletonColumns = [
    "Invoice #",
    "PO #",
    "Supplier",
    "Received by",
    "Date",
    "Actions",
  ];

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div>
        <div className="space-y-5 px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/receiving-invoices/new"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Receiving
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Receiving Invoices</li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <AuditHistoriesLink slug="receiving-invoice" />
              <Link
                to="/receiving-invoices/new"
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A]"
              >
                <span className="text-lg leading-none">+</span>
                Add receiving
              </Link>
            </div>
          </header>

          <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <OperationalTableToolbar
              className=""
              searchRowClassName=""
              footer={null}
              searchRow={
                <>
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    name="searchReceivingInvoice"
                    id="searchReceivingInvoice"
                    className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                    placeholder="Search by supplier name"
                    ref={searchRef}
                    onChange={scheduleSearch}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }
                        handleSearch();
                      }
                    }}
                  />
                </>
              }
              filterActions={
                <DateRangeFilter
                  onChange={(startDate: any, endDate: any) =>
                    setFilters({
                      ...filters,
                      startDate,
                      endDate,
                    })
                  }
                  onClear={clearDates}
                  endDate={filters.endDate}
                  startDate={filters.startDate}
                  setShowDates={setShowDates}
                  showDates={showDates}
                  applyChanges={applyDateFilter}
                  maxDate={new Date()}
                  rangeSummary={hasDateFilter ? rangeSummary : undefined}
                />
              }
              trailing={
                <>
                  <span className="text-xs font-medium text-[#64748B]">
                    {resultMeta}
                  </span>
                  {hasAnyFilters ? (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={handleClearAll}
                      className="!min-h-0 text-xs"
                    >
                      Clear all
                    </Button>
                  ) : null}
                </>
              }
            />

            <div className="operational-embedded-table overflow-hidden">
              {loading ? (
                <OperationalDataTableSkeleton
                  columns={tableSkeletonColumns}
                  rows={10}
                />
              ) : (
                <DataTable
                  value={receivingInvoices?.data ?? []}
                  dataKey="id"
                  className="p-datatable-sm text-sm"
                  emptyMessage={emptyMessage}
                >
                  <Column
                    header="Invoice #"
                    body={invoiceNumberBody}
                    style={{ minWidth: "8rem" }}
                    bodyClassName="font-semibold text-[#0F172A]"
                  />
                  <Column
                    header="PO #"
                    body={poNumberBody}
                    style={{ minWidth: "8rem" }}
                    bodyClassName="font-semibold text-[#0F172A]"
                  />
                  <Column
                    header="Supplier"
                    body={supplierBody}
                    style={{ minWidth: "12rem" }}
                  />
                  <Column
                    header="Received by"
                    body={receivedByBody}
                    style={{ minWidth: "10rem" }}
                  />
                  <Column
                    header="Date"
                    body={dateBody}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Actions"
                    body={actionsBody}
                    style={{ minWidth: "11rem" }}
                  />
                </DataTable>
              )}
            </div>

            <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
              <Paginator
                className="operational-embedded-paginator"
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (_state: unknown) => {
  return {};
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar: any) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AllReceivingInvoices),
);
